/**
 * Material calculators catalogue: search, categories, recently used
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { EmptyState, IconTile, Page, PageHeader } from '../ui';
import { useLanguage } from '../../utils/LanguageContext';
import { CATEGORIES } from '../../utils/materialEstimatorData';
import {
  ALL_CALCULATORS,
  CATEGORY_ICONS,
  CATEGORY_ORDER,
  getRecentCalculators,
  matchesSearch,
} from './calculatorMeta';

const text = {
  en: {
    title: 'Material calculators',
    subtitle: `${ALL_CALCULATORS.length} calculators for bricks, concrete, tiles, roofing and more.`,
    search: 'Search, e.g. brick, simen, tiles',
    all: 'All',
    recent: 'Recently used',
    noResults: 'No calculators found',
    noResultsDesc: 'Try another word, in English or Bahasa Malaysia.',
  },
  my: {
    title: 'Kalkulator bahan',
    subtitle: `${ALL_CALCULATORS.length} kalkulator untuk bata, konkrit, jubin, bumbung dan lain-lain.`,
    search: 'Cari, cth. bata, simen, jubin',
    all: 'Semua',
    recent: 'Baru digunakan',
    noResults: 'Tiada kalkulator dijumpai',
    noResultsDesc: 'Cuba perkataan lain, dalam Bahasa Malaysia atau English.',
  },
};

export const CalculatorCard = ({ calc, language, onClick, dense = false }) => {
  const my = language === 'my';
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', p: dense ? { xs: 1.25, sm: 1.5 } : 1.75, '& > .MuiStack-root': { width: '100%' } }}>
        <Stack
          direction={dense ? { xs: 'column', sm: 'row' } : 'row'}
          spacing={dense ? { xs: 1, sm: 1.5 } : 1.5}
          alignItems={dense ? { xs: 'flex-start', sm: 'center' } : 'center'}
        >
          <IconTile icon={CATEGORY_ICONS[calc.categoryId]} size={dense ? 34 : 40} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
              {my ? calc.labelMy : calc.label}
            </Typography>
            {!dense && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {my ? calc.category.labelMy : calc.category.label}
              </Typography>
            )}
          </Box>
          <ChevronRightRoundedIcon color="action" fontSize="small" sx={{ display: dense ? { xs: 'none', sm: 'block' } : 'block' }} />
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const CalculatorsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const my = language === 'my';
  const t = text[language] || text.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [recent] = useState(getRecentCalculators);

  const category = CATEGORIES[searchParams.get('category')] ? searchParams.get('category') : 'all';
  const setCategory = (id) => setSearchParams(id === 'all' ? {} : { category: id }, { replace: true });

  const results = useMemo(
    () => ALL_CALCULATORS.filter((c) => (category === 'all' || c.categoryId === category) && matchesSearch(c, query)),
    [category, query]
  );

  const grouped = CATEGORY_ORDER.map((catId) => ({
    catId,
    items: results.filter((c) => c.categoryId === catId),
  })).filter((g) => g.items.length);

  const open = (id) => navigate(`/app/calculators/${id}`);

  return (
    <Page maxWidth="lg">
      <PageHeader title={t.title} subtitle={t.subtitle} />

      <TextField
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t.search}
        inputProps={{ 'aria-label': t.search }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 3, overflowX: 'auto', pb: 0.5, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 }, '&::-webkit-scrollbar': { display: 'none' } }}
      >
        <Chip label={t.all} color={category === 'all' ? 'primary' : 'default'} variant={category === 'all' ? 'filled' : 'outlined'} onClick={() => setCategory('all')} />
        {CATEGORY_ORDER.map((catId) => (
          <Chip
            key={catId}
            icon={CATEGORY_ICONS[catId]}
            label={my ? CATEGORIES[catId].labelMy : CATEGORIES[catId].label}
            color={category === catId ? 'primary' : 'default'}
            variant={category === catId ? 'filled' : 'outlined'}
            onClick={() => setCategory(catId)}
            sx={{ flexShrink: 0, '& .MuiChip-icon': { fontSize: 18 } }}
          />
        ))}
      </Stack>

      {!query && category === 'all' && recent.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <HistoryRoundedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              {t.recent}
            </Typography>
          </Stack>
          <Grid container spacing={1.5}>
            {recent.slice(0, 4).map((c) => (
              <Grid item xs={12} sm={6} md={3} key={c.id}>
                <CalculatorCard calc={c} language={language} onClick={() => open(c.id)} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {grouped.length === 0 ? (
        <EmptyState icon={<SearchOffRoundedIcon />} title={t.noResults} description={t.noResultsDesc} />
      ) : (
        grouped.map(({ catId, items }) => (
          <Box key={catId} sx={{ mb: 3.5 }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="h6" component="h2">
                {my ? CATEGORIES[catId].labelMy : CATEGORIES[catId].label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {my ? CATEGORIES[catId].descriptionMy : CATEGORIES[catId].description}
              </Typography>
            </Box>
            <Grid container spacing={1.5}>
              {items.map((c) => (
                <Grid item xs={6} md={4} key={c.id}>
                  <CalculatorCard calc={c} language={language} onClick={() => open(c.id)} dense />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))
      )}
    </Page>
  );
};

export default CalculatorsPage;
